    import React, { useEffect, useState, useCallback } from 'react';
import { 
Table, 
Input, 
Button, 
Space, 
Typography, 
Tag,
Row,
Col,
Radio
} from 'antd';
    import { 
    SearchOutlined
    } from '@ant-design/icons';
    import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
    import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
    import type { ColumnsType } from 'antd/es/table';
    import { adminAcademyApi } from '@/services/api';
import { formatNumber } from '@/utils/number';

const { Title } = Typography;
const DEFAULT_PAGE_SIZE = 10;

const REGISTRATION_FILTER_MAP: Record<'all' | 'active' | 'pending' | 'suspended', string | undefined> = {
  all: undefined,
  active: 'APPROVED',
  pending: 'PENDING',
  suspended: 'WITHDRAWN',
};

type ContractStatusFilter =
  | 'all'
  | 'free_usage'
  | 'payment_waiting'
  | 'payment_active'
  | 'payment_failed'
  | 'billing_hold'
  | 'cancelled';

const CONTRACT_FILTER_MAP: Record<ContractStatusFilter, string | undefined> = {
  all: undefined,
  free_usage: 'free_usage',
  payment_waiting: 'payment_waiting',
  payment_active: 'payment_active',
  payment_failed: 'payment_failed',
  billing_hold: 'billing_hold',
  cancelled: 'cancelled',
};

const CONTRACT_STATUS_CONFIG: Record<string, { text: string; color: string }> = {
  free_usage: { text: '무료사용', color: '#1890ff' },
  payment_waiting: { text: '결제대기', color: '#fa8c16' },
  payment_active: { text: '결제중', color: '#52c41a' },
  payment_failed: { text: '미납', color: '#f5222d' },
  billing_hold: { text: '유예기간', color: '#722ed1' },
  cancelled: { text: '해지', color: '#8c8c8c' },
};

const formatCurrency = (value?: number) => `${formatNumber(value ?? 0)}원`;

interface AcademyData {
    key: string;
    no: number;
    academyCode: string;
    academyName: string;
registrationStatus: 'active' | 'pending' | 'suspended' | 'rejected';
contractStatus: string;
contractStatusKey?: ContractStatusFilter;
contractStatusLabel?: string;
overdueCount?: number;
overdueAmount?: number;
    studentCount: number;
    teacherCount: number;
    registrationDate: string;
    }

const AcademyList: React.FC = () => {
const navigate = useNavigate();
const location = useLocation();
const [searchParams, setSearchParams] = useSearchParams();
const initialKeyword = searchParams.get('q') || '';
const regParam = (searchParams.get('reg') || '').toUpperCase();
const contractParam = searchParams.get('contract') || '';
const initialStatusFilter: 'all' | 'active' | 'pending' | 'suspended' =
  regParam === 'APPROVED' ? 'active' :
  regParam === 'PENDING' ? 'pending' :
  regParam === 'WITHDRAWN' ? 'suspended' : 'all';
const initialContractFilter: ContractStatusFilter = (Object.keys(CONTRACT_FILTER_MAP) as ContractStatusFilter[]).includes(contractParam as ContractStatusFilter)
  ? contractParam as ContractStatusFilter
  : (Object.entries(CONTRACT_FILTER_MAP).find(([, v]) => v === contractParam)?.[0] as ContractStatusFilter) || 'all';
const initialPage = Number(searchParams.get('page')) || 1;
const initialPageSize = Number(searchParams.get('size')) || DEFAULT_PAGE_SIZE;
const [loading, setLoading] = useState(false);
const [searchText, setSearchText] = useState(initialKeyword);
const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'pending' | 'suspended'>(initialStatusFilter);
const [contractFilter, setContractFilter] = useState<ContractStatusFilter>(initialContractFilter);
const [filters, setFilters] = useState<{ q: string; registrationStatus?: string; contractStatus?: string }>({
  q: initialKeyword,
  registrationStatus: REGISTRATION_FILTER_MAP[initialStatusFilter],
  contractStatus: CONTRACT_FILTER_MAP[initialContractFilter],
});

const [dataSource, setDataSource] = useState<AcademyData[]>([]);
const [pageTotal, setPageTotal] = useState(0);
const [page, setPage] = useState(initialPage);
const [pageSize, setPageSize] = useState(initialPageSize);

const syncSearchParams = useCallback((nextPage: number, nextSize: number, nextFilters: { q: string; registrationStatus?: string; contractStatus?: string }) => {
  const params = new URLSearchParams();
  if (nextFilters.q) params.set('q', nextFilters.q);
  const regEntry = Object.entries(REGISTRATION_FILTER_MAP).find(([k, v]) => v === nextFilters.registrationStatus);
  if (regEntry && regEntry[0] !== 'all') params.set('reg', regEntry[1] as string);
  const contractEntry = Object.entries(CONTRACT_FILTER_MAP).find(([k, v]) => v === nextFilters.contractStatus);
  if (contractEntry && contractEntry[0] !== 'all') params.set('contract', contractEntry[1] as string);
  if (nextPage > 1) params.set('page', String(nextPage));
  if (nextSize !== DEFAULT_PAGE_SIZE) params.set('size', String(nextSize));
  setSearchParams(params);
}, [setSearchParams]);

useEffect(() => {
    const load = async () => {
        setLoading(true);
        try {
            const res = await adminAcademyApi.list({
                q: filters.q || undefined,
                registrationStatus: filters.registrationStatus,
                contractStatus: filters.contractStatus,
                page: page - 1,
                size: pageSize,
            });
            const pageData = res?.data || res;
            const content = pageData?.content || pageData?.page?.content || [];
            const total = pageData?.totalElements || pageData?.page?.totalElements || content.length;
            const mapped: AcademyData[] = content.map((item: any, idx: number) => ({
                key: String(total - ((page-1)*pageSize + idx)),
                no: total - ((page-1)*pageSize + idx),
                academyCode: item.academyCode,
                academyName: item.academyName,
                registrationStatus: (item.registrationStatus || 'active').toLowerCase(),
                contractStatus: (item.contractStatus || 'active').toLowerCase(),
                contractStatusKey: item.contractStatusKey || undefined,
                contractStatusLabel: item.contractStatusLabel || undefined,
                overdueCount: item.overdueCount ?? 0,
                overdueAmount: item.overdueAmount ?? 0,
                studentCount: item.studentCount || 0,
                teacherCount: item.teacherCount || 0,
                registrationDate: item.registrationDate || '',
            }));
            setDataSource(mapped);
            setPageTotal(total);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };
    load();
}, [page, pageSize, filters]);

useEffect(() => {
  syncSearchParams(page, pageSize, filters);
}, [page, pageSize, filters, syncSearchParams]);

// 검색 처리
const handleSearch = () => {
    setPage(1);
    setFilters(prev => ({ ...prev, q: searchText.trim() }));
};

// 검색 초기화
const handleReset = () => {
    setSearchText('');
    setStatusFilter('all');
    setContractFilter('all');
    setPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
    setFilters({ q: '' });
};

const handleRegistrationFilterChange = (value: 'all' | 'active' | 'pending' | 'suspended') => {
    setStatusFilter(value);
    setPage(1);
    setFilters(prev => ({ ...prev, registrationStatus: REGISTRATION_FILTER_MAP[value] }));
};

const handleContractFilterChange = (value: ContractStatusFilter) => {
    setContractFilter(value);
    setPage(1);
    setFilters(prev => ({ ...prev, contractStatus: CONTRACT_FILTER_MAP[value] }));
};

    // 신규 등록 제거: 관리자 측에서 학원 등록 기능 미사용


    // 테이블 컬럼 정의
    const academyColumns: ColumnsType<AcademyData> = [
        {
        title: '순번',
        dataIndex: 'no',
        key: 'no',
        width: 80,
        align: 'center',
        render: (no: number) => formatNumber(no),
        },
        {
        title: '학원코드',
        dataIndex: 'academyCode',
        key: 'academyCode',
        width: 120,
        align: 'center',
        },
        {
        title: '학원명',
        dataIndex: 'academyName',
        key: 'academyName',
        width: 200,
        },
        {
        title: '가입상태',
        dataIndex: 'registrationStatus',
        key: 'registrationStatus',
        width: 120,
        align: 'center',
        render: (status: string) => {
            const normalized = (status || '').toUpperCase();
            const statusMap = {
            APPROVED: { color: 'green', text: '승인' },
            PENDING: { color: 'orange', text: '승인대기' },
            REJECTED: { color: 'red', text: '반려' },
            WITHDRAWN: { color: 'default', text: '탈퇴' },
            } as const;
            const mapped = (statusMap as any)[normalized] || { color: 'default', text: status || '-' };
            return <Tag color={mapped.color}>{mapped.text}</Tag>;
        },
        },
        {
        title: '계약상태',
        dataIndex: 'contractStatusKey',
        key: 'contractStatusKey',
        width: 160,
        align: 'center',
        render: (_: any, record: AcademyData) => {
            const key = record.contractStatusKey || record.contractStatus?.toLowerCase() || 'payment_active';
            const config = CONTRACT_STATUS_CONFIG[key] || { text: record.contractStatusLabel || key, color: '#595959' };
            let label = record.contractStatusLabel || config.text;
            if (key === 'payment_failed' && (record.overdueCount || record.overdueAmount)) {
                const count = record.overdueCount || 0;
                const amount = record.overdueAmount || 0;
                label = `${config.text} (${formatNumber(count)}회 / ${formatCurrency(amount)})`;
            }
            return (
                <span style={{ color: config.color, fontWeight: 600 }}>
                    {label}
                </span>
            );
        },
        },
        {
        title: '원생 수',
        dataIndex: 'studentCount',
        key: 'studentCount',
        width: 100,
        align: 'center',
        render: (count: number) => formatNumber(count),
        },
        {
        title: '강사 수',
        dataIndex: 'teacherCount',
        key: 'teacherCount',
        width: 100,
        align: 'center',
        render: (count: number) => formatNumber(count),
        },
        {
        title: '가입승인일',
        dataIndex: 'registrationDate',
        key: 'registrationDate',
        width: 120,
        align: 'center',
        },

    ];

    return (
        <AdminLayout>
            <div style={{ padding: '24px' }}>
                <Row gutter={16} justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
                    <Title level={2}>
                        학원 관리
                    </Title>
                </Row>

                {/* 검색 영역 */}
                <Row gutter={16} align="middle" style={{ marginBottom: '16px' }}>
                    <Col span={6}>
                        <Input
                            placeholder="학원명, 학원코드로 검색"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onPressEnter={handleSearch}
                            prefix={<SearchOutlined />}
                            size="large"
                        />
                    </Col>
                    <Col>
                        <Space>
                            <Button type="primary" onClick={handleSearch} icon={<SearchOutlined />} size="large">
                                검색
                            </Button>
                            <Button onClick={handleReset} size="large">
                                초기화
                            </Button>
                        </Space>
                    </Col>
                </Row>

                {/* 필터 영역 */}
                <Row style={{ marginBottom: '16px' }}>
                    <Col>
                        <Space size="large">
                            <div>
                                <span style={{ marginRight: '8px', fontWeight: 'bold' }}>가입상태:</span>
                                <Radio.Group 
                                    value={statusFilter} 
                                    onChange={(e) => handleRegistrationFilterChange(e.target.value)}
                                    size="large"
                                >
                                    <Radio.Button value="all">전체</Radio.Button>
                                    <Radio.Button value="active">활성</Radio.Button>
                                    <Radio.Button value="pending">대기</Radio.Button>
                                    <Radio.Button value="suspended">정지</Radio.Button>
                                </Radio.Group>
                            </div>
                            <div>
                                <span style={{ marginRight: '8px', fontWeight: 'bold' }}>계약상태:</span>
                                <Radio.Group 
                                    value={contractFilter} 
                                    onChange={(e) => handleContractFilterChange(e.target.value)}
                                    size="large"
                                >
                                    <Radio.Button value="all">전체</Radio.Button>
                                    <Radio.Button value="free_usage">무료사용</Radio.Button>
                                    <Radio.Button value="payment_waiting">결제대기</Radio.Button>
                                    <Radio.Button value="payment_active">결제중</Radio.Button>
                                    <Radio.Button value="payment_failed">미납</Radio.Button>
                                    <Radio.Button value="billing_hold">유예기간</Radio.Button>
                                    <Radio.Button value="cancelled">해지</Radio.Button>
                                </Radio.Group>
                            </div>
                        </Space>
                    </Col>
                </Row>

                {/* 테이블 */}
                <Table
                    columns={academyColumns}
                    dataSource={dataSource}
                    pagination={{
                        total: pageTotal,
                        current: page,
                        pageSize: pageSize,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => {
                            if (!total) return `0-0 / 총 ${formatNumber(0)}개`;
                            const start = Math.max(total - range[1] + 1, 1);
                            const end = Math.max(total - range[0] + 1, 1);
                            return `${formatNumber(start)}-${formatNumber(end)} / 총 ${formatNumber(total)}개`;
                        },
                        onChange: (p, ps) => { setPage(p); setPageSize(ps); },
                    }}
                    scroll={{ x: 1000 }}
                    onRow={(record) => ({
                        onClick: () => navigate(
                            `/admin/academy/detail/${record.academyCode}`,
                            { state: { from: `${location.pathname}${location.search}` } },
                        ),
                        style: { cursor: 'pointer' }
                    })}
                />
            </div>
        </AdminLayout>
    );
    };

    export default AcademyList; 